/**
 * Encodeur de QR code (ISO/IEC 18004), réduit à ce dont l'enrôlement du double
 * facteur a besoin (AB#296) : mode octet, correction d'erreur M, versions 1 à
 * 10 — soit jusqu'à 213 octets, quand une URI `otpauth://` en fait 120.
 *
 * ─── Pourquoi pas une bibliothèque ─────────────────────────────────────────
 *
 * Une dépendance de plus, c'est une ligne de plus dans le fichier de verrou
 * partagé par tout le monorepo, et un paquet tiers de plus dans la console
 * qui détient les sessions administrateur. Le QR code, lui, est un algorithme
 * public et stable depuis 2000 : deux cents lignes sans réseau, sans `eval`,
 * sans rien à mettre à jour.
 *
 * ─── Ce que ce module NE fait PAS ──────────────────────────────────────────
 *
 * Le secret ne quitte jamais le navigateur pour être dessiné : pas de service
 * d'images en ligne (« chart.googleapis.com/…?chl=otpauth://… »), qui
 * recevrait en clair le second facteur d'un administrateur.
 *
 * La structure suit la référence de Project Nayuki (licence MIT), réécrite.
 */

/** Matrice de modules : `true` = module sombre. `[ligne][colonne]`. */
export type MatriceQr = boolean[][];

const VERSION_MAX = 10;

// Correction d'erreur niveau M (~15 %), versions 1..10 (index 0 inutilisé).
const ECC_PAR_BLOC = [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26];
const NB_BLOCS = [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5];
/** Bits de format du niveau M (L = 1, M = 0, Q = 3, H = 2). */
const FORMAT_ECC_M = 0;

function bit(x: number, i: number): boolean {
  return ((x >>> i) & 1) !== 0;
}

/** Nombre de modules de données (hors motifs de fonction) d'une version. */
function modulesDeDonnees(version: number): number {
  let n = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const nbAlign = Math.floor(version / 7) + 2;
    n -= (25 * nbAlign - 10) * nbAlign - 55;
    if (version >= 7) n -= 36;
  }
  return n;
}

function octetsDeDonnees(version: number): number {
  return Math.floor(modulesDeDonnees(version) / 8) - ECC_PAR_BLOC[version]! * NB_BLOCS[version]!;
}

/* ─── Reed-Solomon sur GF(2^8), polynôme 0x11D ─────────────────────────── */

function multiplier(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z;
}

function diviseur(degre: number): number[] {
  const r = new Array<number>(degre).fill(0);
  r[degre - 1] = 1;
  let racine = 1;
  for (let i = 0; i < degre; i++) {
    for (let j = 0; j < r.length; j++) {
      r[j] = multiplier(r[j]!, racine);
      if (j + 1 < r.length) r[j]! ^= r[j + 1]!;
    }
    racine = multiplier(racine, 0x02);
  }
  return r;
}

function reste(donnees: readonly number[], div: readonly number[]): number[] {
  const r = div.map(() => 0);
  for (const b of donnees) {
    const facteur = b ^ r.shift()!;
    r.push(0);
    div.forEach((coef, i) => {
      r[i]! ^= multiplier(coef, facteur);
    });
  }
  return r;
}

/* ─── Codage des données ─────────────────────────────────────────────────── */

function codewords(octets: readonly number[], version: number): number[] {
  const bits: number[] = [];
  const pousser = (valeur: number, longueur: number) => {
    for (let i = longueur - 1; i >= 0; i--) bits.push((valeur >>> i) & 1);
  };
  pousser(0b0100, 4); // mode octet
  pousser(octets.length, version <= 9 ? 8 : 16);
  for (const o of octets) pousser(o, 8);

  const capacite = octetsDeDonnees(version) * 8;
  pousser(0, Math.min(4, capacite - bits.length)); // terminateur
  pousser(0, (8 - (bits.length % 8)) % 8);
  for (let rembourrage = 0xec; bits.length < capacite; rembourrage ^= 0xec ^ 0x11) {
    pousser(rembourrage, 8);
  }

  const donnees: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    donnees.push(bits.slice(i, i + 8).reduce((acc, b) => (acc << 1) | b, 0));
  }

  // Découpage en blocs, correction d'erreur par bloc, entrelacement.
  const nbBlocs = NB_BLOCS[version]!;
  const eccLong = ECC_PAR_BLOC[version]!;
  const total = Math.floor(modulesDeDonnees(version) / 8);
  const nbCourts = nbBlocs - (total % nbBlocs);
  const longCourt = Math.floor(total / nbBlocs);
  const div = diviseur(eccLong);
  const blocs: number[][] = [];
  for (let i = 0, k = 0; i < nbBlocs; i++) {
    const dat = donnees.slice(k, k + longCourt - eccLong + (i < nbCourts ? 0 : 1));
    k += dat.length;
    const ecc = reste(dat, div);
    if (i < nbCourts) dat.push(0);
    blocs.push(dat.concat(ecc));
  }
  const resultat: number[] = [];
  for (let i = 0; i < blocs[0]!.length; i++) {
    blocs.forEach((bloc, j) => {
      if (i !== longCourt - eccLong || j >= nbCourts) resultat.push(bloc[i]!);
    });
  }
  return resultat;
}

/* ─── Placement ──────────────────────────────────────────────────────────── */

function positionsAlignement(version: number, taille: number): number[] {
  if (version === 1) return [];
  const nb = Math.floor(version / 7) + 2;
  const pas = Math.ceil((version * 4 + 4) / (nb * 2 - 2)) * 2;
  const r = [6];
  for (let pos = taille - 7; r.length < nb; pos -= pas) r.splice(1, 0, pos);
  return r;
}

function masque(m: number, x: number, y: number): boolean {
  switch (m) {
    case 0:
      return (x + y) % 2 === 0;
    case 1:
      return y % 2 === 0;
    case 2:
      return x % 3 === 0;
    case 3:
      return (x + y) % 3 === 0;
    case 4:
      return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
    case 5:
      return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6:
      return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    default:
      return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
  }
}

class Grille {
  readonly modules: MatriceQr;
  readonly fonction: boolean[][];

  constructor(
    readonly version: number,
    readonly taille = version * 4 + 17,
  ) {
    this.modules = Array.from({ length: taille }, () => new Array<boolean>(taille).fill(false));
    this.fonction = Array.from({ length: taille }, () => new Array<boolean>(taille).fill(false));
  }

  poser(x: number, y: number, sombre: boolean): void {
    this.modules[y]![x] = sombre;
    this.fonction[y]![x] = true;
  }

  motifsDeFonction(): void {
    const t = this.taille;
    for (let i = 0; i < t; i++) {
      this.poser(6, i, i % 2 === 0);
      this.poser(i, 6, i % 2 === 0);
    }
    for (const [cx, cy] of [
      [3, 3],
      [t - 4, 3],
      [3, t - 4],
    ] as const) {
      for (let dy = -4; dy <= 4; dy++) {
        for (let dx = -4; dx <= 4; dx++) {
          const d = Math.max(Math.abs(dx), Math.abs(dy));
          const x = cx + dx;
          const y = cy + dy;
          if (x >= 0 && x < t && y >= 0 && y < t) this.poser(x, y, d !== 2 && d !== 4);
        }
      }
    }
    const pos = positionsAlignement(this.version, t);
    const n = pos.length;
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if ((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0)) continue;
        for (let dy = -2; dy <= 2; dy++) {
          for (let dx = -2; dx <= 2; dx++) {
            this.poser(pos[i]! + dx, pos[j]! + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
          }
        }
      }
    }
    this.format(0); // réserve les emplacements ; réécrit après le masque
    if (this.version >= 7) {
      let r = this.version;
      for (let i = 0; i < 12; i++) r = (r << 1) ^ ((r >>> 11) * 0x1f25);
      const bits = (this.version << 12) | r;
      for (let i = 0; i < 18; i++) {
        const a = t - 11 + (i % 3);
        const b = Math.floor(i / 3);
        this.poser(a, b, bit(bits, i));
        this.poser(b, a, bit(bits, i));
      }
    }
  }

  format(m: number): void {
    const t = this.taille;
    const donnees = (FORMAT_ECC_M << 3) | m;
    let r = donnees;
    for (let i = 0; i < 10; i++) r = (r << 1) ^ ((r >>> 9) * 0x537);
    const bits = ((donnees << 10) | r) ^ 0x5412;
    for (let i = 0; i <= 5; i++) this.poser(8, i, bit(bits, i));
    this.poser(8, 7, bit(bits, 6));
    this.poser(8, 8, bit(bits, 7));
    this.poser(7, 8, bit(bits, 8));
    for (let i = 9; i < 15; i++) this.poser(14 - i, 8, bit(bits, i));
    for (let i = 0; i < 8; i++) this.poser(t - 1 - i, 8, bit(bits, i));
    for (let i = 8; i < 15; i++) this.poser(8, t - 15 + i, bit(bits, i));
    this.poser(8, t - 8, true); // module sombre
  }

  donnees(octets: readonly number[]): void {
    const t = this.taille;
    let i = 0;
    for (let droite = t - 1; droite >= 1; droite -= 2) {
      if (droite === 6) droite = 5;
      for (let v = 0; v < t; v++) {
        for (let j = 0; j < 2; j++) {
          const x = droite - j;
          const montant = ((droite + 1) & 2) === 0;
          const y = montant ? t - 1 - v : v;
          if (!this.fonction[y]![x] && i < octets.length * 8) {
            this.modules[y]![x] = bit(octets[i >>> 3]!, 7 - (i & 7));
            i++;
          }
        }
      }
    }
  }

  appliquerMasque(m: number): void {
    for (let y = 0; y < this.taille; y++) {
      for (let x = 0; x < this.taille; x++) {
        if (!this.fonction[y]![x] && masque(m, x, y)) {
          this.modules[y]![x] = !this.modules[y]![x];
        }
      }
    }
  }

  /**
   * Pénalité simplifiée (règles 1, 2 et 4 de la norme). Tout masque donne un
   * code lisible ; la pénalité ne sert qu'à choisir le plus confortable pour
   * un appareil photo. La règle 3 (faux motifs de repérage) est omise.
   */
  penalite(): number {
    const t = this.taille;
    let p = 0;
    for (let a = 0; a < t; a++) {
      for (const horizontal of [true, false]) {
        let run = 1;
        for (let b = 1; b <= t; b++) {
          const cour = b < t ? (horizontal ? this.modules[a]![b] : this.modules[b]![a]) : undefined;
          const prec = horizontal ? this.modules[a]![b - 1] : this.modules[b - 1]![a];
          if (cour === prec) run++;
          else {
            if (run >= 5) p += run - 2;
            run = 1;
          }
        }
      }
    }
    let sombres = 0;
    for (let y = 0; y < t; y++) {
      for (let x = 0; x < t; x++) {
        const c = this.modules[y]![x];
        if (c) sombres++;
        if (
          x < t - 1 &&
          y < t - 1 &&
          c === this.modules[y]![x + 1] &&
          c === this.modules[y + 1]![x] &&
          c === this.modules[y + 1]![x + 1]
        ) {
          p += 3;
        }
      }
    }
    p += Math.floor(Math.abs(sombres * 20 - t * t * 10) / (t * t)) * 10;
    return p;
  }
}

function utf8(texte: string): number[] {
  return Array.from(new TextEncoder().encode(texte));
}

/**
 * Encode `texte` dans le plus petit QR code (niveau M) qui le contient.
 * Lève si le texte dépasse la version 10.
 */
export function encoderQr(texte: string): MatriceQr {
  const octets = utf8(texte);
  let version = 1;
  while (version <= VERSION_MAX) {
    const entete = 4 + (version <= 9 ? 8 : 16);
    if (entete + octets.length * 8 <= octetsDeDonnees(version) * 8) break;
    version++;
  }
  if (version > VERSION_MAX) throw new Error('Texte trop long pour ce QR code.');

  const donnees = codewords(octets, version);
  let meilleure: Grille | null = null;
  let meilleurScore = Infinity;
  for (let m = 0; m < 8; m++) {
    const g = new Grille(version);
    g.motifsDeFonction();
    g.donnees(donnees);
    g.appliquerMasque(m);
    g.format(m);
    const score = g.penalite();
    if (score < meilleurScore) {
      meilleure = g;
      meilleurScore = score;
    }
  }
  return meilleure!.modules;
}
